/**
 * Song library API: songs, files (metadata + raw part uploads + commit) and collections.
 *
 * Shared query keys (contract between teams):
 *   ['songs'] → SongListItemDTO[]   ['song', id] → SongDTO
 *   ['collections'] → CollectionDTO[]   ['collection', id] → CollectionDTO
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  COLLECTION_CHANGED,
  type CollectionDTO,
  type CommitFileRequest,
  type CreateFileRequest,
  type MediaPart,
  type SetCollectionEntriesRequest,
  type SongDTO,
  type SongFileDTO,
  type SongListItemDTO,
  type UpdateFileRequest,
  type UpsertCollectionRequest,
  type UpsertSongRequest,
} from '../../shared/dto/library';
import type { EventDTO } from '../../shared/dto/events';
import { practiceEventId, practiceList, type PracticeListDTO } from '../../shared/program';
import { api, apiRaw, ApiFailure } from './client';

export const SONGS_KEY = ['songs'] as const;
export const songKey = (id: string) => ['song', id] as const;
export const COLLECTIONS_KEY = ['collections'] as const;
export const collectionKey = (id: string) => ['collection', id] as const;

/* ─── Response unwrapping ──────────────────────────────────────────────────
 * Accept both a bare DTO and a one-key envelope ({ songs: [...] }, { song: {...} }) so the
 * client keeps working whichever shape the route returns. Anything else is a contract error. */

type Guard = (v: unknown) => boolean;
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isList: Guard = (v) => Array.isArray(v);
const isSong: Guard = (v) => isObj(v) && typeof v.id === 'string' && Array.isArray(v.files);
const isCollection: Guard = (v) => isObj(v) && typeof v.id === 'string' && Array.isArray(v.entries);
const isFile: Guard = (v) => isObj(v) && typeof v.id === 'string' && typeof v.rev === 'number' && typeof v.kind === 'string';

function unwrap<T>(r: unknown, key: string, guard: Guard): T {
  if (guard(r)) return r as T;
  if (isObj(r) && guard(r[key])) return r[key] as T;
  throw new Error(`unexpected response shape (${key})`);
}

/** Like unwrap, but returns null when the response carries no such object (e.g. 204 / { ok: true }). */
function maybeUnwrap<T>(r: unknown, key: string, guard: Guard): T | null {
  if (guard(r)) return r as T;
  if (isObj(r) && guard(r[key])) return r[key] as T;
  return null;
}

/* ─── Fetchers ─────────────────────────────────────────────────────────── */

export async function fetchSongs(signal?: AbortSignal): Promise<SongListItemDTO[]> {
  return unwrap<SongListItemDTO[]>(await api<unknown>('/songs', { signal }), 'songs', isList);
}

export async function fetchSong(id: string, signal?: AbortSignal): Promise<SongDTO> {
  return unwrap<SongDTO>(await api<unknown>(`/songs/${encodeURIComponent(id)}`, { signal }), 'song', isSong);
}

export async function fetchCollections(signal?: AbortSignal): Promise<CollectionDTO[]> {
  return unwrap<CollectionDTO[]>(await api<unknown>('/collections', { signal }), 'collections', isList);
}

/** A folder / setlist — or, for a practice list id (p-<eventId>, shared/program.ts), the event's songs to practise. */
export async function fetchCollection(id: string, signal?: AbortSignal): Promise<CollectionDTO> {
  const eventId = practiceEventId(id);
  if (eventId) return unwrap<PracticeListDTO>(await api<unknown>(`/events/${encodeURIComponent(eventId)}/program`, { signal }), 'program', isCollection);
  return unwrap<CollectionDTO>(await api<unknown>(`/collections/${encodeURIComponent(id)}`, { signal }), 'collection', isCollection);
}

/**
 * A practice list built on the device from the cached event (calendar lists or the single event —
 * keys kept literal here, see api/calendar.ts) and the cached song / folder / setlist lists (as this
 * user got them): opens instantly, and offline. Undefined when the event is not cached.
 */
export function cachedPracticeList(qc: QueryClient, eventId: string): PracticeListDTO | undefined {
  let ev = qc.getQueryData<EventDTO>(['event', eventId]);
  if (!ev) for (const [, list] of qc.getQueriesData<EventDTO[]>({ queryKey: ['events'] })) ev ??= list?.find((e) => e.id === eventId);
  if (!ev || !Array.isArray(ev.program)) return undefined;
  const songs = new Set((qc.getQueryData<SongListItemDTO[]>(SONGS_KEY) ?? []).map((s) => s.id));
  return practiceList(ev, ev.program, qc.getQueryData<CollectionDTO[]>(COLLECTIONS_KEY) ?? [], songs, 0);
}

/* ─── Plain request functions (also used by the upload queue) ──────────── */

export type SongPatch = Omit<UpsertSongRequest, 'id'>;
export type CollectionPatch = Partial<Omit<UpsertCollectionRequest, 'id'>>;

export async function createSong(req: UpsertSongRequest): Promise<void> {
  await api<unknown>('/songs', { method: 'POST', body: req });
}

export async function updateSong(id: string, patch: SongPatch): Promise<void> {
  await api<unknown>(`/songs/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export async function deleteSong(id: string): Promise<void> {
  await api<unknown>(`/songs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** POST /files (idempotent on the client UUID). Returns the file record when the route echoes it. */
export async function createFile(req: CreateFileRequest): Promise<SongFileDTO | null> {
  return maybeUnwrap<SongFileDTO>(await api<unknown>('/files', { method: 'POST', body: req }), 'file', isFile);
}

export async function updateFile(id: string, patch: UpdateFileRequest): Promise<void> {
  await api<unknown>(`/files/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export async function deleteFile(id: string): Promise<void> {
  await api<unknown>(`/files/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Upload timeout scaled to size (assumes ≥ 25 kB/s on a bad connection). */
export function uploadTimeoutMs(bytes: number): number {
  return 60_000 + Math.ceil(bytes / 25_000) * 1000;
}

/** PUT /files/:id/rev/:rev/:part — raw body; the browser sets Content-Length. */
export async function putFilePart(
  id: string,
  rev: number,
  part: MediaPart,
  body: Blob,
  contentType: string,
  signal?: AbortSignal,
): Promise<void> {
  await apiRaw(`/files/${encodeURIComponent(id)}/rev/${rev}/${part}`, {
    method: 'PUT',
    raw: body,
    rawType: contentType,
    signal,
    timeoutMs: uploadTimeoutMs(body.size),
  });
}

export async function commitFile(id: string, req: CommitFileRequest, signal?: AbortSignal): Promise<void> {
  await api<unknown>(`/files/${encodeURIComponent(id)}/commit`, { method: 'POST', body: req, signal, timeoutMs: 30_000 });
}

export async function createCollection(req: UpsertCollectionRequest): Promise<void> {
  await api<unknown>('/collections', { method: 'POST', body: req });
}

/** PATCH /collections/:id. Returns the updated collection when the route echoes it. */
export async function updateCollection(id: string, patch: CollectionPatch): Promise<CollectionDTO | null> {
  return maybeUnwrap<CollectionDTO>(await api<unknown>(`/collections/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch }), 'collection', isCollection);
}

export async function deleteCollection(id: string): Promise<void> {
  await api<unknown>(`/collections/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * A save with `baseUpdatedAt` hit a collection that changed since the list was loaded (another
 * editor saved, or it was deleted): nothing was written. Still an ApiFailure (409 conflict), so
 * generic error texts keep working.
 */
export class CollectionChangedError extends ApiFailure {
  constructor(details?: unknown) {
    super(409, 'conflict', 'collection changed', details);
  }
}

const isCollectionChanged = (e: unknown): boolean =>
  e instanceof ApiFailure && e.status === 409 && isObj(e.details) && e.details.reason === COLLECTION_CHANGED;

/** PUT /collections/:id/entries — the whole ordered list. Returns the saved collection when echoed. */
export async function setCollectionEntries(id: string, req: SetCollectionEntriesRequest): Promise<CollectionDTO | null> {
  try {
    return maybeUnwrap<CollectionDTO>(
      await api<unknown>(`/collections/${encodeURIComponent(id)}/entries`, { method: 'PUT', body: req }),
      'collection',
      isCollection,
    );
  } catch (e) {
    if (isCollectionChanged(e)) throw new CollectionChangedError((e as ApiFailure).details);
    throw e;
  }
}

/* ─── Cache helpers ────────────────────────────────────────────────────── */

export function invalidateSong(qc: QueryClient, songId: string): Promise<unknown> {
  return Promise.all([qc.invalidateQueries({ queryKey: songKey(songId) }), qc.invalidateQueries({ queryKey: SONGS_KEY })]);
}

/** Collections changed: lists, single collections, and songs (their collectionIds). */
export function invalidateCollections(qc: QueryClient): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: COLLECTIONS_KEY }),
    qc.invalidateQueries({ queryKey: ['collection'] }),
    qc.invalidateQueries({ queryKey: SONGS_KEY }),
    qc.invalidateQueries({ queryKey: ['song'] }),
  ]);
}

function patchSongFiles(qc: QueryClient, songId: string, fn: (files: SongFileDTO[]) => SongFileDTO[]) {
  qc.setQueryData<SongDTO>(songKey(songId), (old) => (old ? { ...old, files: fn(old.files) } : old));
}

/* ─── Queries ──────────────────────────────────────────────────────────── */

export function useSongs() {
  return useQuery({
    queryKey: SONGS_KEY,
    queryFn: ({ signal }) => fetchSongs(signal),
    meta: { persist: true },
  });
}

export function useSong(id: string | undefined) {
  return useQuery({
    queryKey: songKey(id ?? ''),
    queryFn: ({ signal }) => fetchSong(id!, signal),
    enabled: !!id,
    meta: { persist: true },
  });
}

export function useCollections() {
  return useQuery({
    queryKey: COLLECTIONS_KEY,
    queryFn: ({ signal }) => fetchCollections(signal),
    meta: { persist: true },
  });
}

export interface CollectionQueryOptions {
  /** Poll while mounted (e.g. 60 s while a song of the list is open; TanStack pauses it in the background / offline). */
  refetchInterval?: number;
  /** 'always' = refetch on every mount even when fresh (the shared order may have changed). */
  refetchOnMount?: boolean | 'always';
}

/**
 * Single collection; seeded from the persisted list so it opens instantly (and offline). A practice
 * list (p-<eventId>) is seeded from the cached event and lists instead, as stale (fetched at once).
 */
export function useCollection(id: string | undefined, opts: CollectionQueryOptions = {}) {
  const qc = useQueryClient();
  const eventId = id ? practiceEventId(id) : null;
  return useQuery({
    queryKey: collectionKey(id ?? ''),
    queryFn: ({ signal }) => fetchCollection(id!, signal),
    enabled: !!id,
    meta: { persist: true },
    initialData: () => (eventId ? cachedPracticeList(qc, eventId) : qc.getQueryData<CollectionDTO[]>(COLLECTIONS_KEY)?.find((c) => c.id === id)),
    initialDataUpdatedAt: () => (eventId ? 0 : qc.getQueryState(COLLECTIONS_KEY)?.dataUpdatedAt),
    ...(opts.refetchInterval !== undefined ? { refetchInterval: opts.refetchInterval } : {}),
    ...(opts.refetchOnMount !== undefined ? { refetchOnMount: opts.refetchOnMount } : {}),
  });
}

/** Puts a collection the server just returned into both caches (single + list). */
export function storeCollection(qc: QueryClient, dto: CollectionDTO) {
  qc.setQueryData<CollectionDTO>(collectionKey(dto.id), dto);
  qc.setQueryData<CollectionDTO[]>(COLLECTIONS_KEY, (old) => old?.map((c) => (c.id === dto.id ? dto : c)));
}

/* ─── Mutations ────────────────────────────────────────────────────────── */

export function useCreateSong() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSong,
    onSuccess: () => qc.invalidateQueries({ queryKey: SONGS_KEY }),
  });
}

export function useUpdateSong(songId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SongPatch) => updateSong(songId, patch),
    onSuccess: (_r, patch) => {
      qc.setQueryData<SongDTO>(songKey(songId), (old) => (old ? { ...old, ...patch } as SongDTO : old));
      return invalidateSong(qc, songId);
    },
  });
}

export function useDeleteSong() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSong,
    onSuccess: (_r, songId) => {
      qc.removeQueries({ queryKey: songKey(songId), exact: true });
      qc.setQueryData<SongListItemDTO[]>(SONGS_KEY, (old) => old?.filter((s) => s.id !== songId));
      return invalidateCollections(qc);
    },
  });
}

/** PATCH file metadata with an optimistic update of ['song', songId]. */
export function useUpdateFile(songId: string) {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `song-files:${songId}` }, // run one PATCH at a time so the last tap wins
    mutationFn: ({ id, patch }: { id: string; patch: UpdateFileRequest; mask?: number }) => updateFile(id, patch),
    onMutate: async ({ id, patch, mask }) => {
      await qc.cancelQueries({ queryKey: songKey(songId) });
      const prev = qc.getQueryData<SongDTO>(songKey(songId));
      patchSongFiles(qc, songId, (files) =>
        files.map((f) =>
          f.id === id
            ? {
                ...f,
                ...(patch.title !== undefined ? { title: patch.title } : {}),
                ...(patch.sort !== undefined ? { sort: patch.sort } : {}),
                ...(patch.lyricsText !== undefined ? { lyricsText: patch.lyricsText } : {}),
                ...(mask !== undefined ? { audienceMask: mask } : {}),
              }
            : f,
        ),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(songKey(songId), ctx.prev);
    },
    onSettled: () => invalidateSong(qc, songId),
  });
}

/** Persist a new order for some files of one song (sequential PATCHes, optimistic). */
export function useReorderFiles(songId: string) {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: `song-files:${songId}` },
    mutationFn: async (updates: { id: string; sort: number }[]) => {
      for (const u of updates) await updateFile(u.id, { sort: u.sort });
    },
    onMutate: async (updates) => {
      await qc.cancelQueries({ queryKey: songKey(songId) });
      const prev = qc.getQueryData<SongDTO>(songKey(songId));
      const bySort = new Map(updates.map((u) => [u.id, u.sort]));
      patchSongFiles(qc, songId, (files) => files.map((f) => (bySort.has(f.id) ? { ...f, sort: bySort.get(f.id)! } : f)));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(songKey(songId), ctx.prev);
    },
    onSettled: () => invalidateSong(qc, songId),
  });
}

export function useDeleteFile(songId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: (_r, fileId) => {
      patchSongFiles(qc, songId, (files) => files.filter((f) => f.id !== fileId));
      return invalidateSong(qc, songId);
    },
  });
}

export function useCreateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCollection,
    onSuccess: () => invalidateCollections(qc),
  });
}

export function useUpdateCollection(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: CollectionPatch) => updateCollection(id, patch),
    onSuccess: (saved, patch) => {
      // The echoed collection carries the new updatedAt (an open list editor rebases its draft on it).
      if (saved) {
        storeCollection(qc, saved);
        return invalidateCollections(qc);
      }
      const apply = (c: CollectionDTO): CollectionDTO => ({
        ...c,
        ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.description !== undefined ? { description: patch.description } : {}),
        ...(patch.sort !== undefined ? { sort: patch.sort } : {}),
      });
      qc.setQueryData<CollectionDTO>(collectionKey(id), (old) => (old ? apply(old) : old));
      qc.setQueryData<CollectionDTO[]>(COLLECTIONS_KEY, (old) => old?.map((c) => (c.id === id ? apply(c) : c)));
      return invalidateCollections(qc);
    },
  });
}

export function useDeleteCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteCollection,
    onSuccess: (_r, id) => {
      qc.removeQueries({ queryKey: collectionKey(id), exact: true });
      qc.setQueryData<CollectionDTO[]>(COLLECTIONS_KEY, (old) => old?.filter((c) => c.id !== id));
      return invalidateCollections(qc);
    },
  });
}

/**
 * Saves the whole ordered list. Pass `baseUpdatedAt` (the updatedAt the list was built from) so a
 * save over someone else's newer change fails with CollectionChangedError instead of erasing it.
 */
export function useSetCollectionEntries(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: SetCollectionEntriesRequest) => setCollectionEntries(id, req),
    onSuccess: (saved, req) => {
      // Show the saved order immediately: the server's answer (with the new updatedAt) when echoed,
      // else the sent order (positions are only compared relative to each other).
      if (saved) storeCollection(qc, saved);
      else {
        const entries = req.entries.map((e, i) => ({ entryId: e.entryId, songId: e.songId, note: e.note ?? null, position: i }));
        qc.setQueryData<CollectionDTO>(collectionKey(id), (old) => (old ? { ...old, entries } : old));
        qc.setQueryData<CollectionDTO[]>(COLLECTIONS_KEY, (old) => old?.map((c) => (c.id === id ? { ...c, entries } : c)));
      }
      return invalidateCollections(qc);
    },
    // Someone else changed the list: fetch the latest everywhere (not awaited, so the caller's own
    // onError — toast, reset its draft — runs at once).
    onError: (e) => {
      if (e instanceof CollectionChangedError) void invalidateCollections(qc);
    },
  });
}
